/**
 * How far through something is, as a bar with its own accessible value.
 *
 * @module components/ui/ProgressBar
 */

import React from "react";

export const PROGRESS_TONES = [
  "primary",
  "success",
  "warning",
  "error",
  "info",
  "neutral",
] as const;
export type ProgressTone = (typeof PROGRESS_TONES)[number];

const TONE_CLASS: Record<ProgressTone, string> = {
  primary: "progress-primary",
  success: "progress-success",
  warning: "progress-warning",
  error: "progress-error",
  info: "progress-info",
  // The neutral token is the track's own grey in the dark theme, so the fill
  // is the ink colour instead.
  neutral: "text-base-content",
};

export interface ProgressBarProps {
  value: number;
  max?: number;
  /** What is being counted, for the bar's accessible name. */
  label: string;
  tone?: ProgressTone;
  className?: string;
  "data-testid"?: string;
}

/** Narrows a tone to the bar's own palette; anything else reads as primary. */
export function progressTone(tone: string): ProgressTone {
  return (PROGRESS_TONES as readonly string[]).includes(tone) ? (tone as ProgressTone) : "primary";
}

export function ProgressBar({
  value,
  max = 100,
  label,
  tone = "primary",
  className = "",
  "data-testid": testId,
}: ProgressBarProps): React.ReactElement {
  return (
    <progress
      className={`progress bg-base-300 ${TONE_CLASS[tone]} ${className}`.trim()}
      value={value}
      max={max}
      aria-label={label}
      data-testid={testId}
    />
  );
}

export default ProgressBar;
